import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

export function boolParam(params: URLSearchParams, name: string): boolean | undefined {
    const value = params.get(name);
    return value === null ? undefined : value === "true";
}

// Deep-link filter seeding shared by the list pages: a tile or link elsewhere carries filter values as URL
// params (produced by useUrlProvider). Apply them to the page filter exactly once, after the saved views resolve
// so we override, not lose, them, then strip the params so a later manual filter change or a refresh doesn't
// keep re-applying the deep-link. Everything seeded lands as visible filter chips, nothing is applied behind
// the user's back.
export function useSeedFilterFromUrl(isReady: boolean, paramNames: Array<string>, seed: (params: URLSearchParams) => void) {
    const [searchParams, setSearchParams] = useSearchParams();
    const hasSeeded = useRef(false);
    useEffect(() => {
        if (!isReady || hasSeeded.current) return;
        if (!paramNames.some((name) => searchParams.has(name))) return;

        hasSeeded.current = true;
        seed(searchParams);

        // Stripped off the live params rather than off the snapshot above: the operational tab seeds twice on one
        // landing (the section on the page, the filter in the table that mounts), and effects run child-first, so a
        // snapshot taken before the other seed's write would put the params it just stripped back in the url.
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                paramNames.forEach((name) => next.delete(name));
                return next;
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady]);
}
